import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { MainLayout } from "../../components/Layout/MainLayout";
import { Text } from "../../components/Text/Text";
import { Button } from "../../components/common/Button/Button";
import { Stepper } from "../../components/Stepper/Stepper";

import "./contact.scss";

import staticData from "../../translations/en/application.json";
import staticDataAr from "../../translations/ar/application.json";

import { SelectSubject } from "./forms/selectSubject";
import { CareersFields } from "./forms/careersFields";
import { CommonFields } from "./forms/commonFields";
import { MaintenanceFields } from "./forms/maintenanceFields";
import { ReturnsFields } from "./forms/returnsSecondForm";
import { AssistWithOnlinePurchase } from "./forms/assistWithOnlinePurchase";
import { ComplaintSubmissionForm } from "./forms/complaintFields";
import { BusinessSalesForm } from "./forms/businessSales";
import { OpportunitiesSubmissionForm } from "./forms/opportunitiesForm";
import { TrainingProgramSubmissionForm } from "./forms/trainingProgramSubmissionForm";
import { OtherInquiriesSubmissionForm } from "./forms/otherInquiryFormSubmission";

import { countriesList } from "../../shared/countrylist";
import { getMainSiteUrl } from "../../helpers/siteUrls";

function createOptions(activeTab) {
  // Maintenance and complaint options live in *_a / *_b, not contact[activeTab].
  if (activeTab === "complaint" || activeTab === "maintenance") {
    const variants = ["_a", "_b"];
    const ar = variants.map((el) => staticDataAr.contact[activeTab + el]);
    const en = variants.map((el) => staticData.contact[activeTab + el]);

    if (!en.every((item) => item) || !ar.every((item) => item)) {
      return [];
    }

    return variants.map((_variant, i) =>
      Object.entries(en[i] || {}).map(([key, val]) => ({
        value: key,
        label: val,
        translationName: {
          en: val,
          ar: ar[i]?.[key] || val,
        },
      }))
    );
  }

  const ar = staticDataAr.contact[activeTab];
  const en = staticData.contact[activeTab];

  if (!en || !ar) {
    return [];
  }

  return Object.entries(en || {}).map(([key, val]) => ({
    value: key,
    label: val,
    translationName: {
      en: val,
      ar: ar?.[key] || val,
    },
  }));
}

const RenderStep = ({
  answers,
  setAnswers,
  t,
  resume,
  invoiceRef,
  photoRef,
  careerOptions,
  setSubmitted,
  currentStep,
  setStep,
}) => {
  const { msgSubject } = answers;

  if (currentStep === 1) {
    return (
      <div className="form-card">
        <SelectSubject
          answers={answers}
          options={createOptions("tabs")}
          setAnswers={setAnswers}
          placeholder={t("contact.type.label")}
          nextStep={setStep}
        />
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div className="form-card">
        {(() => {
          switch (msgSubject) {
            case "career_request":
              return (
                <CareersFields
                  answers={answers}
                  setAnswers={setAnswers}
                  resume={resume}
                  options={careerOptions}
                  nextStep={() => setStep(3)}
                />
              );

            case "maintenance":
              return (
                <MaintenanceFields
                  answers={answers}
                  setAnswers={setAnswers}
                  invoiceRef={invoiceRef}
                  photoRef={photoRef}
                  options={createOptions("maintenance")}
                  nextStep={() => setStep(3)}
                />
              );

            case "complaint":
              return (
                <SelectSubject
                  name="type"
                  answers={answers}
                  setAnswers={setAnswers}
                  options={createOptions("complaint")[0]}
                  placeholder={t("contact.type.label3")}
                  nextStep={() => setStep(3)}
                />
              );

            case "returns":
              // Returns doesn't need step 2, skip directly to step 3
              return null;

            default:
              // Only call createOptions if the data exists
              const options = createOptions(msgSubject);
              if (!options || options.length === 0) {
                // If no options available, skip to step 3
                return null;
              }
              return (
                <SelectSubject
                  name="type"
                  answers={answers}
                  setAnswers={setAnswers}
                  options={options}
                  placeholder={t("contact.type.label2")}
                  nextStep={() => setStep(3)}
                />
              );
          }
        })()}
      </div>
    );
  }

  if (currentStep === 3) {
    return (
      <div className="form-card">
        {(() => {
          switch (msgSubject) {
            case "career_request":
              return (
                <CommonFields
                  setSubmitted={setSubmitted}
                  answers={answers}
                  resume={resume.current}
                  options={countriesList}
                />
              );

            case "returns":
              return (
                <ReturnsFields
                  setSubmitted={setSubmitted}
                  answers={answers}
                  photoRef={photoRef}
                  options={countriesList}
                />
              );

            case "assist_online_purchase":
              return (
                <AssistWithOnlinePurchase
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                />
              );

            case "business_sales":
              return (
                <BusinessSalesForm
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                />
              );

            case "opportunities":
              return (
                <OpportunitiesSubmissionForm
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                  photoRef={photoRef}
                />
              );

            case "training":
              return (
                <TrainingProgramSubmissionForm
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                />
              );

            case "other_inquiries":
              return (
                <OtherInquiriesSubmissionForm
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                />
              );

            case "maintenance":
              return (
                <CommonFields
                  setSubmitted={setSubmitted}
                  answers={answers}
                  invoiceRef={invoiceRef.current}
                  photoRef={photoRef.current}
                  options={countriesList}
                />
              );

            case "complaint":
              return (
                <ComplaintSubmissionForm
                  setSubmitted={setSubmitted}
                  answers={answers}
                  options={countriesList}
                  photoRef={photoRef}
                />
              );

            default:
              return null;
          }
        })()}
      </div>
    );
  }

  return null;
};

export const Contact = () => {
  const { t } = useTranslation("application");
  const homeHref = getMainSiteUrl("/");

  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});

  const resume = useRef({});
  const invoiceRef = useRef({});
  const photoRef = useRef({});

  const careerOptions = [
    countriesList,
    createOptions("statuses"),
    createOptions("education"),
  ];

  return (
    <MainLayout className="contact" hideFooter hideHeaderIcons homeHref={homeHref}>
      <Helmet>
        <title>{t("seo.contactUs.title")}</title>
        <meta name="description" content={t("seo.contactUs.description")} />
      </Helmet>

      <header className="contact-header">
        <Text type="headline2">{t("contact.title")}</Text>
        <Text type="body1">{t("contact.header")}</Text>
      </header>

      {submitted ? (
        <div className="success-state">
          <Text type="body1">{t("contact.thanks")}</Text>
          <Button type="naked" onClick={() => { window.location.href = homeHref; }}>
            {t("notFound.goBackToHomepage")}
          </Button>
        </div>
      ) : (
        <Stepper layout="horizontal">
          {["step1", "step2", "step3"].map((step, index) => (
            <RenderStep
              key={step}
              currentStep={index + 1}
              setStep={() => { }}
              answers={answers}
              setAnswers={setAnswers}
              t={t}
              resume={resume}
              invoiceRef={invoiceRef}
              photoRef={photoRef}
              careerOptions={careerOptions}
              setSubmitted={setSubmitted}
            />
          ))}
        </Stepper>
      )}
    </MainLayout>
  );
};
